import { getOrganizationApplicationTemplate } from 'admin/actions/merchant-application-templates';
import {
  approveOrganizationApplication,
  getOrganizationApplication,
  holdOrganizationApplication,
  OrganizationApplication,
  rejectOrganizationApplication,
  reviewOrganizationApplication,
  updateOrganizationApplication,
} from 'admin/actions/merchant-applications';
import { getOrganizationApplicationSettingOrNull } from 'admin/actions/organization';
import { getActiveUserTagGroupsWithItems, NormalizedTagGroup } from 'admin/actions/user-tag-group';
import { Button } from 'admin/components/atoms/Buttons';
import Grid from 'admin/components/atoms/Grid';
import Label from 'admin/components/atoms/Label';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import {
  DecisionBar,
  DecisionButton,
  DecisionGuide,
  DiscardChangesModal,
  isReviewEditable,
  ReviewStepper,
  StepperHeader,
  TerminalNote,
} from 'admin/components/organisms/ReviewFlow';
import NotificationToast from 'admin/components/molecules/NotificationToast';
import TagSelectionEditor, { TagSelection } from 'admin/components/organisms/TagSelectionEditor';
import { USAGE_TYPE_OPTIONS, UsageType } from 'admin/constants/usageType';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import styled, { css } from 'styled-components';
import {
  hideLoadingSpinner,
  showLoadingSpinner,
} from '~/javascripts/admin/actions/loading-spinner';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { toJapaneseBankingSystemHalfWidth } from '~/javascripts/admin/utils/characters/toHalfWidth';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import { validateEmail } from '~/javascripts/admin/utils/email';
import DateTimeRaw from '../../atoms/DateTime';
import Headings from '../../atoms/Headings';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextBox from '../../atoms/TextBox';
import Tooltip from '../../atoms/Tooltip';
import ConfirmModal from './ConfirmModal';

// 付与中の item id 群を、所属グループ単位の TagSelection に集約する（有効グループのみ）。
const buildSelectionsFromItemIds = (
  groups: NormalizedTagGroup[],
  itemIds: string[]
): TagSelection[] =>
  groups
    .map((group) => ({
      groupId: group.id,
      itemIds: itemIds.filter((id) => group.items.some((it) => it.id === id)),
    }))
    .filter((row) => row.itemIds.length > 0);

// テンプレートのスカラー項目オプション（紹介元/資本金/従業員数）の取りうる値。
type FieldOption = 'required' | 'optional' | 'hidden';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '決済加盟店管理', href: '', isCurrentPage: true },
  { value: '決済加盟店申請管理', href: '/organizations/application', isCurrentPage: false },
  { value: '決済加盟店申請詳細', href: '', isCurrentPage: true },
];

// 状態遷移・保存の成功時に上部トーストで出す文言（submitResult のキーに対応）。
const SUBMIT_MESSAGES = {
  'approve-success': '申請を承認しました',
  'reject-success': '申請を却下しました',
  'review-success': '審査を開始しました',
  'resume-success': '審査を再開しました',
  'hold-success': '申請を保留にしました',
  'save-success': '申請を保存しました',
} as const;

const Wrapper = styled.div`
  margin-top: 24px;
`;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    ${(props) => props.theme.typography.body2.regular}
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const SectionLabel = styled(Label)`
  font-weight: 600;
`;

const ValueText = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    color: ${theme.semanticColors.text.primary};
  `}
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const FieldError = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    margin: 2px 0;
    color: ${theme.semanticColors.accent.red};
  `}
`;

const InactiveTagNote = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #999;
`;

const WarningMessage = styled.div`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    margin: 16px 0;
    padding: 12px 16px;
    background-color: ${theme.colors.yellow[50]};
    border: 1px solid ${theme.colors.yellow[300]};
    border-radius: ${theme.borderRadius[4]};
    color: ${theme.colors.yellow[700]};
  `}
`;

const DateTime = styled(DateTimeRaw)`
  margin-bottom: 0;
`;

type ApplicationStatus = 'submitted' | 'reviewing' | 'pending' | 'approved' | 'rejected';

type PrivateMoneyOption = {
  label: string;
  value: string;
};

type ShopFormValues = {
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  // 店舗形態（店舗ごと・フロント必須）。空文字は未選択。
  usage_type: UsageType | '';
};

// 店舗ラベルの編集状態。selections は編集中の行（空行も保持）、inactiveIds は
// 無効化済みで表示のみ（更新時に外れる）、originalIds は取得失敗時の保全用。
type ShopTagState = {
  selections: TagSelection[];
  inactiveIds: string[];
  originalIds: string[];
};

type FormValues = {
  status: ApplicationStatus;
  business_type: 'individual' | 'corporate';
  private_money_ids: string[];
  name: string;
  furigana: string;
  corporate_number: string;
  representative_name: string;
  representative_furigana: string;
  representative_birth_date: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  capital: string;
  number_of_employees: string;
  referral_source: string;
  bank_name: string;
  bank_code: string;
  bank_branch_name: string;
  bank_branch_code: string;
  bank_account_type: 'saving' | 'current';
  bank_account: string;
  bank_account_holder_name: string;
  shops: ShopFormValues[];
};

const MerchantApplicationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const [application, setApplication] = useState<OrganizationApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitResult, setSubmitResult] = useState<
    | 'approve-success'
    | 'reject-success'
    | 'review-success'
    | 'resume-success'
    | 'hold-success'
    | 'save-success'
    | 'error'
    | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAutoNumberingAvailable, setIsAutoNumberingAvailable] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 変更の破棄（未保存の編集を最後の保存状態へ戻す）の確認ダイアログ
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(null);
  const [originalValues, setOriginalValues] = useState<FormValues | null>(null);
  // 審査時に発行体が店舗ラベルを編集できるよう、発行体orgの有効グループを保持する
  const [availableTagGroups, setAvailableTagGroups] = useState<NormalizedTagGroup[]>([]);
  // タググループ取得に失敗したか（失敗時は既存ラベルを誤って消さないようガードする）
  const [tagGroupsLoadFailed, setTagGroupsLoadFailed] = useState(false);
  // テンプレートで必須(field_option=required)指定された有効グループのid
  const [requiredTagGroupIds, setRequiredTagGroupIds] = useState<string[]>([]);
  // テンプレートの紹介元/資本金/従業員数の項目オプション（required/optional/hidden）。
  // 公開フォームと同様に必須マーク・入力検証・非表示を詳細画面でも反映するため保持する。
  // 既定は optional（テンプレ取得失敗時も既存フィールドを消さないセーフフォールバック）。
  const [scalarFieldOptions, setScalarFieldOptions] = useState<{
    referral_source: FieldOption;
    capital: FieldOption;
    number_of_employees: FieldOption;
  }>({ referral_source: 'optional', capital: 'optional', number_of_employees: 'optional' });
  // 店舗ラベル必須チェックのエラーメッセージ（行ごと）
  const [tagValidationErrors, setTagValidationErrors] = useState<string[]>([]);
  // 店舗ごとの店舗ラベル編集状態（RHFのフラットid配列だと空行を保持できないため別管理）
  const [shopTags, setShopTags] = useState<ShopTagState[]>([]);

  const setShopSelections = (shopIndex: number, selections: TagSelection[]) =>
    setShopTags((prev) => prev.map((st, i) => (i === shopIndex ? { ...st, selections } : st)));
  // 無効ラベル（有効グループに解決できない付与item）の名称表示用
  const [tagItemNameById, setTagItemNameById] = useState<Record<string, string>>({});

  const {
    control,
    reset,
    watch,
    getValues,
    trigger,
    formState: { errors, isDirty: formIsDirty },
  } = useForm<FormValues>({
    defaultValues: {
      status: 'submitted',
      business_type: 'individual',
      private_money_ids: [],
      name: '',
      furigana: '',
      corporate_number: '',
      representative_name: '',
      representative_furigana: '',
      representative_birth_date: '',
      postal_code: '',
      address: '',
      tel: '',
      email: '',
      capital: '',
      number_of_employees: '',
      bank_name: '',
      bank_code: '',
      bank_branch_name: '',
      bank_branch_code: '',
      bank_account_type: 'saving',
      bank_account: '',
      bank_account_holder_name: '',
      shops: [],
    },
  });

  // Private money options
  const issuedMoneys = user.issuedMoneys().filter((x: any) => x.type !== 'own');
  const privateMoneyOptions: PrivateMoneyOption[] = issuedMoneys.map((money: any) => ({
    label: money.name,
    value: money.id,
  }));

  // Get the current form values for private_money_ids to display names in modal
  const currentPrivateMoneyIds = watch('private_money_ids');
  const selectedPrivateMoneyNames = privateMoneyOptions
    .filter((opt) => currentPrivateMoneyIds.includes(opt.value))
    .map((opt) => opt.label);

  // Watch business_type for conditional rendering
  const businessType = watch('business_type');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [applicationRes, settingRes] = await Promise.all([
          getOrganizationApplication(id),
          getOrganizationApplicationSettingOrNull(),
        ]);

        setApplication(applicationRes);

        // 審査時に選択できる店舗ラベルは「テンプレートで表示対象(非hidden)かつ有効なグループ」に限定する。
        // （サーバの提案店舗バリデーションが、テンプレートの有効オプショングループ配下の item のみ許容するため）
        let groups: NormalizedTagGroup[] = [];
        let requiredIds: string[] = [];
        try {
          const template = await getOrganizationApplicationTemplate(applicationRes.template_id);
          // 紹介元/資本金/従業員数の必須・非表示をテンプレートに合わせて反映する。
          setScalarFieldOptions({
            referral_source: template.referral_source_field_option ?? 'optional',
            capital: template.capital_field_option ?? 'optional',
            number_of_employees: template.number_of_employees_field_option ?? 'optional',
          });
          if (template.issuer_organization_code) {
            const allActive = await getActiveUserTagGroupsWithItems(
              template.issuer_organization_code
            );
            const options = template.user_tag_group_options ?? [];
            const optionGroupIds = new Set(
              options.filter((o) => o.field_option !== 'hidden').map((o) => o.user_tag_group_id)
            );
            groups = allActive.filter((g) => optionGroupIds.has(g.id));
            // 必須グループ（有効なオプショングループのうち field_option=required）
            const groupIdSet = new Set(groups.map((g) => g.id));
            requiredIds = options
              .filter((o) => o.field_option === 'required' && groupIdSet.has(o.user_tag_group_id))
              .map((o) => o.user_tag_group_id);
          }
        } catch (e) {
          console.error('Failed to load user tag groups', e);
          setTagGroupsLoadFailed(true);
        }
        setAvailableTagGroups(groups);
        setRequiredTagGroupIds(requiredIds);

        // 店舗ごとの店舗ラベル編集状態を構築（head-office を先頭に additional を続ける順）
        const isResolvable = (idv: string) =>
          groups.some((g) => g.items.some((it) => it.id === idv));
        const buildShopTagState = (s: typeof applicationRes.head_office_shop): ShopTagState => {
          const ids = (s?.user_tag_group_items ?? []).map((it) => it.id);
          return {
            selections: buildSelectionsFromItemIds(groups, ids),
            inactiveIds: ids.filter((idv) => !isResolvable(idv)),
            originalIds: ids,
          };
        };
        const builtShopTags: ShopTagState[] = [];
        if (applicationRes.head_office_shop) {
          builtShopTags.push(buildShopTagState(applicationRes.head_office_shop));
        }
        (applicationRes.additional_shops ?? []).forEach((s) => {
          builtShopTags.push(buildShopTagState(s));
        });
        setShopTags(builtShopTags);

        // 付与item の名称マップ（無効ラベル表示用）を構築
        const nameById: Record<string, string> = {};
        const collectNames = (s: typeof applicationRes.head_office_shop) =>
          (s?.user_tag_group_items ?? []).forEach((it) => {
            nameById[it.id] = it.name_ja || it.name_en;
          });
        collectNames(applicationRes.head_office_shop);
        (applicationRes.additional_shops ?? []).forEach(collectNames);
        setTagItemNameById(nameById);

        // If setting doesn't exist, auto-numbering is not available
        const autoNumberingAvailable = settingRes !== null;
        setIsAutoNumberingAvailable(autoNumberingAvailable);

        // Build shops array from head_office_shop and additional_shops
        const shopsData: ShopFormValues[] = [];
        if (applicationRes.head_office_shop) {
          const shop = applicationRes.head_office_shop;
          shopsData.push({
            shop_name: shop.shop_name ?? '',
            shop_name_furigana: shop.shop_name_furigana ?? '',
            postal_code: shop.postal_code ?? '',
            address: shop.address ?? '',
            tel: shop.tel ?? '',
            email: shop.email ?? '',
            contact_person_name: shop.contact_person_name ?? '',
            contact_person_name_furigana: shop.contact_person_name_furigana ?? '',
            usage_type: shop.usage_type ?? '',
          });
        }
        if (applicationRes.additional_shops) {
          applicationRes.additional_shops.forEach((shop) => {
            shopsData.push({
              shop_name: shop.shop_name ?? '',
              shop_name_furigana: shop.shop_name_furigana ?? '',
              postal_code: shop.postal_code ?? '',
              address: shop.address ?? '',
              tel: shop.tel ?? '',
              email: shop.email ?? '',
              contact_person_name: shop.contact_person_name ?? '',
              contact_person_name_furigana: shop.contact_person_name_furigana ?? '',
              usage_type: shop.usage_type ?? '',
            });
          });
        }

        const formValues: FormValues = {
          status: applicationRes.status,
          business_type: applicationRes.business_type,
          private_money_ids: applicationRes.private_money_ids,
          name: applicationRes.name,
          furigana: applicationRes.furigana ?? '',
          corporate_number: applicationRes.corporate_number ?? '',
          representative_name: applicationRes.representative_name ?? '',
          representative_furigana: applicationRes.representative_furigana ?? '',
          representative_birth_date: applicationRes.representative_birth_date ?? '',
          postal_code: applicationRes.postal_code ?? '',
          address: applicationRes.address ?? '',
          tel: applicationRes.tel ?? '',
          email: applicationRes.email ?? '',
          capital: applicationRes.capital != null ? String(applicationRes.capital) : '',
          number_of_employees:
            applicationRes.number_of_employees != null
              ? String(applicationRes.number_of_employees)
              : '',
          referral_source: applicationRes.referral_source ?? '',
          bank_name: applicationRes.bank_name ?? '',
          bank_code: applicationRes.bank_code ?? '',
          bank_branch_name: applicationRes.bank_branch_name ?? '',
          bank_branch_code: applicationRes.bank_branch_code ?? '',
          bank_account_type: applicationRes.bank_account_type ?? 'saving',
          bank_account: applicationRes.bank_account ?? '',
          bank_account_holder_name: applicationRes.bank_account_holder_name ?? '',
          shops: shopsData,
        };
        reset(formValues);
        setOriginalValues(formValues);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, reset]);

  // 必須グループに1件以上ラベルが選択されているか検証する（サーバの必須バリデーションを未然に防ぐ）。
  const validateRequiredTags = (): string[] => {
    if (tagGroupsLoadFailed || requiredTagGroupIds.length === 0) return [];
    const errors: string[] = [];
    shopTags.forEach((st, idx) => {
      requiredTagGroupIds.forEach((gid) => {
        const group = availableTagGroups.find((g) => g.id === gid);
        if (!group) return;
        const selection = st.selections.find((s) => s.groupId === gid);
        const hasItem = !!selection && selection.itemIds.length > 0;
        if (!hasItem) {
          if (group.items.length === 0) {
            errors.push(
              `店舗情報${idx + 1}: 必須の店舗ラベル「${
                group.name
              }」に選択可能なラベルがありません。店舗ラベル管理でラベルを追加してください`
            );
          } else {
            errors.push(`店舗情報${idx + 1}: 店舗ラベル「${group.name}」は必須です`);
          }
        }
      });
    });
    return errors;
  };

  // PATCH ボディを現在のフォーム値から組み立てる。
  const buildUpdateParams = (values: FormValues): Record<string, unknown> => {
    const params: Record<string, unknown> = {
      business_type: values.business_type,
      name: values.name,
      furigana: values.furigana,
      representative_name: values.representative_name,
      representative_furigana: values.representative_furigana,
      representative_birth_date: values.representative_birth_date,
      postal_code: values.postal_code,
      address: values.address,
      tel: values.tel,
      email: values.email,
      referral_source: values.referral_source,
      bank_name: values.bank_name,
      bank_code: values.bank_code,
      bank_branch_name: values.bank_branch_name,
      bank_branch_code: values.bank_branch_code,
      bank_account_type: values.bank_account_type,
      bank_account: values.bank_account,
      bank_account_holder_name: values.bank_account_holder_name,
    };

    // 法人のみの項目（usage_type は店舗ごとに移行済み）
    if (values.business_type === 'corporate') {
      params.corporate_number = values.corporate_number;
      if (values.capital) {
        params.capital = parseInt(values.capital, 10);
      }
      if (values.number_of_employees) {
        params.number_of_employees = parseInt(values.number_of_employees, 10);
      }
    }

    // 店舗ラベルidは shopTags（編集状態）から取り出す。取得失敗時は判定不能のため既存idを保持し、
    // それ以外は有効グループの選択のみ送信（無効化済みラベルはサーバ仕様上除外される）。
    const tagIdsForShop = (i: number): string[] => {
      const st = shopTags[i];
      if (!st) return [];
      return tagGroupsLoadFailed ? st.originalIds : st.selections.flatMap((s) => s.itemIds);
    };

    if (values.shops?.length > 0) {
      params.head_office_shop = {
        shop_name: values.shops[0].shop_name,
        shop_name_furigana: values.shops[0].shop_name_furigana,
        postal_code: values.shops[0].postal_code,
        address: values.shops[0].address,
        tel: values.shops[0].tel,
        email: values.shops[0].email,
        contact_person_name: values.shops[0].contact_person_name,
        contact_person_name_furigana: values.shops[0].contact_person_name_furigana,
        usage_type: values.shops[0].usage_type || null,
        user_tag_group_item_ids: tagIdsForShop(0),
      };
      params.additional_shops = values.shops.slice(1).map((shop, idx) => ({
        shop_name: shop.shop_name,
        shop_name_furigana: shop.shop_name_furigana,
        postal_code: shop.postal_code,
        address: shop.address,
        tel: shop.tel,
        email: shop.email,
        contact_person_name: shop.contact_person_name,
        contact_person_name_furigana: shop.contact_person_name_furigana,
        usage_type: shop.usage_type || null,
        user_tag_group_item_ids: tagIdsForShop(idx + 1),
      }));
    }
    return params;
  };

  // 遷移（と、保存/承認時の PATCH）を実行する中核。可逆遷移はモーダルなしでここへ直接来る。
  const runTransition = async (targetStatus: ApplicationStatus, formValues: FormValues) => {
    const fromStatus = (originalValues?.status ?? application?.status) as ApplicationStatus;
    try {
      dispatch(showLoadingSpinner());

      // 内容(PATCH)を保存するのは「変更を保存」または承認のみ。
      // 審査開始/保留/審査再開はステータスを移すだけ、却下は不備でも成立させたいため PATCH しない。
      const shouldSaveEdits = targetStatus === fromStatus || targetStatus === 'approved';
      if (shouldSaveEdits) {
        await updateOrganizationApplication(id, buildUpdateParams(formValues));
      }

      let updated: OrganizationApplication;
      if (targetStatus === fromStatus) {
        updated = await getOrganizationApplication(id);
        // 保存後は保存した値を新しいベースラインにして未保存フラグ(isDirty)を解消する。
        reset(formValues);
        setOriginalValues(formValues);
        // 保存時、無効ラベルはサーバ側で申請から外れるため UI 上の注記も消す。
        setShopTags((prev) =>
          prev.map((st) => ({
            ...st,
            originalIds: st.selections.flatMap((s) => s.itemIds),
            inactiveIds: [],
          }))
        );
        setSubmitResult('save-success');
      } else if (targetStatus === 'approved') {
        updated = await approveOrganizationApplication(id);
        setSubmitResult('approve-success');
      } else if (targetStatus === 'rejected') {
        updated = await rejectOrganizationApplication(id);
        setSubmitResult('reject-success');
      } else if (targetStatus === 'reviewing') {
        updated = await reviewOrganizationApplication(id);
        // 受付済→審査中は「開始」、保留→審査中は「再開」（ボタン文言と一致させる）。
        setSubmitResult(fromStatus === 'pending' ? 'resume-success' : 'review-success');
      } else {
        updated = await holdOrganizationApplication(id);
        setSubmitResult('hold-success');
      }

      setApplication(updated);
      // 遷移成立後は originalValues の status も更新し、ボタンを現在状態に追従させる（二重送信防止）。
      setOriginalValues((prev) => (prev ? { ...prev, status: updated.status } : prev));
      setErrorMessage(null);
    } catch (e: unknown) {
      setSubmitResult('error');
      // throwOnError は 4xx でパース済み JSON ボディ(Promise)、5xx で文字列を throw する。
      // JSON ボディを解決してバックエンドの message を表示し、汎用エラーで埋もれるのを避ける。
      const body = await Promise.resolve(e).catch(() => null);
      const backendMessage =
        body && typeof body === 'object' && 'message' in body
          ? (body as { message?: string }).message
          : undefined;
      setErrorMessage(backendMessage ?? '処理に失敗しました');
    } finally {
      dispatch(hideLoadingSpinner());
    }
  };

  // 可逆遷移（審査開始 / 審査再開 / 保留）はモーダルなしで即実行する。
  const startReview = () => runTransition('reviewing', getValues());
  const hold = () => runTransition('pending', getValues());

  // 未保存の編集を破棄して最後に保存した状態へ戻す（フォーム項目 + 店舗ラベル）。
  const confirmDiscard = () => {
    if (originalValues) reset(originalValues);
    setShopTags((prev) =>
      prev.map((st) => ({
        ...st,
        selections: buildSelectionsFromItemIds(availableTagGroups, st.originalIds),
      }))
    );
    setTagValidationErrors([]);
    setIsDiscardOpen(false);
  };

  // 内容を PATCH で永続化する（状態は動かさない）。一時保存を許すため必須未入力でも保存できる。
  const handleSave = () => {
    const status = (originalValues?.status ?? application?.status) as ApplicationStatus;
    setTagValidationErrors([]);
    setPendingFormValues({ ...getValues(), status });
    setIsModalOpen(true);
  };

  // 承認は唯一の完全バリデーションゲート（必須・形式・店舗ラベル）。満たさなければ確認へ進めない。
  const handleApprove = async () => {
    const tagErrors = validateRequiredTags();
    const formValid = await trigger();
    setTagValidationErrors(tagErrors);
    if (!formValid || tagErrors.length > 0) return;
    setPendingFormValues({ ...getValues(), status: 'approved' });
    setIsModalOpen(true);
  };

  // 却下は不備そのものが理由になり得るためバリデーションしない。
  const handleReject = () => {
    setTagValidationErrors([]);
    setPendingFormValues({ ...getValues(), status: 'rejected' });
    setIsModalOpen(true);
  };

  // 承認/却下/保存の確認モーダルからの実行。
  const onConfirm = () => {
    if (!pendingFormValues) return;
    setIsModalOpen(false);
    runTransition(pendingFormValues.status, pendingFormValues);
  };

  if (loading) {
    return (
      <>
        <BreadCrumb crumbs={CRUMBS} />
        <div>読み込み中...</div>
      </>
    );
  }

  if (!application) {
    return (
      <>
        <BreadCrumb crumbs={CRUMBS} />
        <div>データが見つかりませんでした</div>
      </>
    );
  }

  // 現在ステータス（保存済み）。遷移可否・編集可否・選択肢の基準。
  const currentStatus = (originalValues?.status ?? application.status) as ApplicationStatus;
  // 確定済み(承認/却下)は遷移・編集不可。
  const isDecided = currentStatus === 'approved' || currentStatus === 'rejected';
  const isEditable = isReviewEditable(currentStatus);
  // 内容編集フィールドの活性可否。編集可能な状態(reviewing)以外は読み取り専用。
  const fieldsDisabled = !isEditable;
  // 未保存の変更があるか。RHF管理外の店舗ラベル編集も含めて判定する。
  const resolvableItemIds = new Set(availableTagGroups.flatMap((g) => g.items.map((it) => it.id)));
  const tagsDirty = shopTags.some((st) => {
    const current = [...st.selections.flatMap((s) => s.itemIds)].sort();
    const original = st.originalIds.filter((idv) => resolvableItemIds.has(idv)).sort();
    return JSON.stringify(current) !== JSON.stringify(original);
  });
  const isDirty = formIsDirty || tagsDirty;

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>決済加盟店申請詳細</Headings.h5>
      <Wrapper>
        {/* ステッパーと文脈アクションを横並びにして縦の占有を抑える */}
        <StepperHeader>
          <ReviewStepper current={currentStatus} />
          {/* 受付済: 最初の一手は「審査を開始」（1クリック・確認なし） */}
          {currentStatus === 'submitted' && (
            <Button type="button" variant="contained" onClick={startReview}>
              審査を開始
            </Button>
          )}
          {/* 保留中: 「審査を再開」（1クリック・確認なし） */}
          {currentStatus === 'pending' && (
            <Button type="button" variant="contained" onClick={startReview}>
              審査を再開
            </Button>
          )}
        </StepperHeader>
        {/* 確定済み: 結果を表示 */}
        {isDecided && <TerminalNote status={currentStatus} entityLabel="加盟店" />}
        <form onSubmit={(e) => e.preventDefault()}>
          <FormGrid>
            <Grid.Item size={2}>
              <SectionLabel>基本情報</SectionLabel>
            </Grid.Item>
            <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
              <Grid.Item flexDirection="column">
                <Label>申請番号</Label>
                <ValueText>{application.id}</ValueText>
              </Grid.Item>
              {!isAutoNumberingAvailable && (
                <Grid.Item flexDirection="column" nextRow size={2}>
                  <WarningMessage>
                    組織コードのプレフィックスが設定されていません。承認するには先に設定を行ってください。
                  </WarningMessage>
                </Grid.Item>
              )}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  事業体の種類
                  <Required />
                </Label>
                <Controller
                  name="business_type"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup>
                      <RadioLabel>
                        <Radio
                          name="businessType"
                          checked={value === 'individual'}
                          setChecked={() => onChange('individual')} disabled={fieldsDisabled}
                        />
                        個人事業主
                      </RadioLabel>
                      <RadioLabel>
                        <Radio
                          name="businessType"
                          checked={value === 'corporate'}
                          setChecked={() => onChange('corporate')} disabled={fieldsDisabled}
                        />
                        法人
                      </RadioLabel>
                    </RadioGroup>
                  )}
                />
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow size={2}>
                <Label>
                  加盟するマネー
                  <Required />
                </Label>
                <Controller
                  name="private_money_ids"
                  control={control}
                  rules={{
                    validate: (value) => value.length > 0 || '加盟するマネーを選択してください',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Select<PrivateMoneyOption, true, never>
                      isMulti
                      isDisabled
                      options={privateMoneyOptions}
                      value={privateMoneyOptions.filter((opt) => value.includes(opt.value))}
                      onChange={(opts) => onChange(opts?.map((opt) => opt.value) ?? [])}
                    />
                  )}
                />
                {errors.private_money_ids && (
                  <FieldError>{errors.private_money_ids.message}</FieldError>
                )}
              </Grid.Item>
              {/* Corporate-only: 法人番号 */}
              {businessType === 'corporate' && (
                <Grid.Item flexDirection="column" nextRow>
                  <Label>
                    法人番号
                    <Required />
                  </Label>
                  <Controller
                    name="corporate_number"
                    control={control}
                    rules={{
                      required: '法人番号を入力してください',
                      pattern: {
                        value: /^\d{13}$/,
                        message: '13桁の数字を入力してください',
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={13} disabled={fieldsDisabled} />
                    )}
                  />
                  {errors.corporate_number && (
                    <FieldError>{errors.corporate_number.message}</FieldError>
                  )}
                </Grid.Item>
              )}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  {businessType === 'individual' ? '屋号（ない場合は氏名）' : '法人名'}
                  <Required />
                </Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required:
                      businessType === 'individual'
                        ? '屋号を入力してください'
                        : '法人名を入力してください',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.name && <FieldError>{errors.name.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  {businessType === 'individual'
                    ? '屋号（フリガナ）'
                    : '法人名（フリガナ）'}
                  <Required />
                </Label>
                <Controller
                  name="furigana"
                  control={control}
                  rules={{
                    required:
                      businessType === 'individual'
                        ? '屋号（フリガナ）を入力してください'
                        : '法人名（フリガナ）を入力してください',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => {
                        const next = toKatakana(value);
                        if (next !== value) onChange(next);
                      }}
                      fullWidth disabled={fieldsDisabled}
                    />
                  )}
                />
                {errors.furigana && <FieldError>{errors.furigana.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  代表者氏名
                  <Required />
                </Label>
                <Controller
                  name="representative_name"
                  control={control}
                  rules={{ required: '代表者氏名を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.representative_name && (
                  <FieldError>{errors.representative_name.message}</FieldError>
                )}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  代表者氏名（フリガナ）
                  <Required />
                </Label>
                <Controller
                  name="representative_furigana"
                  control={control}
                  rules={{ required: '代表者氏名（フリガナ）を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => {
                        const next = toKatakana(value);
                        if (next !== value) onChange(next);
                      }}
                      fullWidth disabled={fieldsDisabled}
                    />
                  )}
                />
                {errors.representative_furigana && (
                  <FieldError>{errors.representative_furigana.message}</FieldError>
                )}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  代表者生年月日
                  <Required />
                </Label>
                <Controller
                  name="representative_birth_date"
                  control={control}
                  rules={{ required: '代表者生年月日を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <DateTime
                      showIcon
                      timeFormat={false}
                      dateFormat="YYYY-MM-DD"
                      inputProps={{
                        placeholder: '日付を選択してください',
                        disabled: fieldsDisabled,
                      }}
                      value={value}
                      onChange={(val) => val && onChange(val.format('YYYY-MM-DD'))}
                    />
                  )}
                />
                {errors.representative_birth_date && (
                  <FieldError>{errors.representative_birth_date.message}</FieldError>
                )}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  郵便番号
                  <Required />
                </Label>
                <Controller
                  name="postal_code"
                  control={control}
                  rules={{
                    required: '郵便番号を入力してください',
                    pattern: {
                      value: /^\d{7}$/,
                      message: '7桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={7} disabled={fieldsDisabled} />
                  )}
                />
                {errors.postal_code && <FieldError>{errors.postal_code.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  {businessType === 'individual'
                    ? '納税地住所（開業届の記載住所）'
                    : '本店所在地（登記住所）'}
                  <Required />
                </Label>
                <Controller
                  name="address"
                  control={control}
                  rules={{
                    required:
                      businessType === 'individual'
                        ? '納税地住所を入力してください'
                        : '本店所在地を入力してください',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.address && <FieldError>{errors.address.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  電話番号
                  <Required />
                </Label>
                <Controller
                  name="tel"
                  control={control}
                  rules={{
                    required: '電話番号を入力してください',
                    pattern: {
                      value: /^\d{10,11}$/,
                      message: '10桁または11桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={11} disabled={fieldsDisabled} />
                  )}
                />
                {errors.tel && <FieldError>{errors.tel.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  メールアドレス
                  <Required />
                </Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'メールアドレスを入力してください',
                    validate: (value) => validateEmail(value),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.email && <FieldError>{errors.email.message}</FieldError>}
              </Grid.Item>
              {/* 個人・法人共通: 紹介元。テンプレートの項目オプションに従い必須/非表示を反映する */}
              {scalarFieldOptions.referral_source !== 'hidden' && (
                <Grid.Item flexDirection="column" nextRow>
                  <Label>
                    紹介元
                    {scalarFieldOptions.referral_source === 'required' && <Required />}
                  </Label>
                  <Controller
                    name="referral_source"
                    control={control}
                    rules={
                      scalarFieldOptions.referral_source === 'required'
                        ? { required: '紹介元を入力してください' }
                        : {}
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={256} disabled={fieldsDisabled} />
                    )}
                  />
                  {errors.referral_source && (
                    <FieldError>{errors.referral_source.message}</FieldError>
                  )}
                </Grid.Item>
              )}
              {/* Corporate-only: 資本金 */}
              {businessType === 'corporate' && scalarFieldOptions.capital !== 'hidden' && (
                <Grid.Item flexDirection="column" nextRow>
                  <Label>
                    資本金（円）
                    {scalarFieldOptions.capital === 'required' && <Required />}
                  </Label>
                  <Controller
                    name="capital"
                    control={control}
                    rules={
                      scalarFieldOptions.capital === 'required'
                        ? { required: '資本金を入力してください' }
                        : {}
                    }
                    render={({ field: { onChange, value } }) => (
                      <TextBox syncValue={value} onChange={onChange} fullWidth type="number" disabled={fieldsDisabled} />
                    )}
                  />
                  {errors.capital && <FieldError>{errors.capital.message}</FieldError>}
                </Grid.Item>
              )}
              {/* Corporate-only: 従業員数 */}
              {businessType === 'corporate' &&
                scalarFieldOptions.number_of_employees !== 'hidden' && (
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      従業員数
                      {scalarFieldOptions.number_of_employees === 'required' && <Required />}
                    </Label>
                    <Controller
                      name="number_of_employees"
                      control={control}
                      rules={
                        scalarFieldOptions.number_of_employees === 'required'
                          ? { required: '従業員数を入力してください' }
                          : {}
                      }
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth type="number" disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.number_of_employees && (
                      <FieldError>{errors.number_of_employees.message}</FieldError>
                    )}
                  </Grid.Item>
                )}
            </Grid.Item>

            <Grid.Divider />

            {/* 振り込み先情報 */}
            <Grid.Item size={2}>
              <SectionLabel>振り込み先情報</SectionLabel>
            </Grid.Item>
            <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  金融機関名
                  <Required />
                </Label>
                <Controller
                  name="bank_name"
                  control={control}
                  rules={{ required: '金融機関名を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.bank_name && <FieldError>{errors.bank_name.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  金融機関コード
                  <Required />
                </Label>
                <Controller
                  name="bank_code"
                  control={control}
                  rules={{
                    required: '金融機関コードを入力してください',
                    pattern: {
                      value: /^\d{4}$/,
                      message: '4桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={4} disabled={fieldsDisabled} />
                  )}
                />
                {errors.bank_code && <FieldError>{errors.bank_code.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  支店名
                  <Required />
                </Label>
                <Controller
                  name="bank_branch_name"
                  control={control}
                  rules={{ required: '支店名を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                  )}
                />
                {errors.bank_branch_name && (
                  <FieldError>{errors.bank_branch_name.message}</FieldError>
                )}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  支店コード
                  <Required />
                </Label>
                <Controller
                  name="bank_branch_code"
                  control={control}
                  rules={{
                    required: '支店コードを入力してください',
                    pattern: {
                      value: /^\d{3}$/,
                      message: '3桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={3} disabled={fieldsDisabled} />
                  )}
                />
                {errors.bank_branch_code && (
                  <FieldError>{errors.bank_branch_code.message}</FieldError>
                )}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  口座種別
                  <Required />
                </Label>
                <Controller
                  name="bank_account_type"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup>
                      <RadioLabel>
                        <Radio
                          name="bankAccountType"
                          checked={value === 'saving'}
                          setChecked={() => onChange('saving')} disabled={fieldsDisabled}
                        />
                        普通
                      </RadioLabel>
                      <RadioLabel>
                        <Radio
                          name="bankAccountType"
                          checked={value === 'current'}
                          setChecked={() => onChange('current')} disabled={fieldsDisabled}
                        />
                        当座
                      </RadioLabel>
                    </RadioGroup>
                  )}
                />
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  口座番号
                  <Required />
                </Label>
                <Controller
                  name="bank_account"
                  control={control}
                  rules={{ required: '口座番号を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={7} disabled={fieldsDisabled} />
                  )}
                />
                {errors.bank_account && <FieldError>{errors.bank_account.message}</FieldError>}
              </Grid.Item>
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  口座名義人
                  <Required />
                </Label>
                <Controller
                  name="bank_account_holder_name"
                  control={control}
                  rules={{ required: '口座名義人を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => {
                        const next = toJapaneseBankingSystemHalfWidth(toKatakana(value));
                        if (next !== value) onChange(next);
                      }}
                      fullWidth disabled={fieldsDisabled}
                    />
                  )}
                />
                {errors.bank_account_holder_name && (
                  <FieldError>{errors.bank_account_holder_name.message}</FieldError>
                )}
              </Grid.Item>
            </Grid.Item>

            {/* 店舗情報 sections */}
            {watch('shops').map((_, shopIndex) => (
              <React.Fragment key={shopIndex}>
                <Grid.Divider />
                <Grid.Item size={2}>
                  <SectionLabel>店舗情報{shopIndex + 1}</SectionLabel>
                </Grid.Item>
                <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
                  <Grid.Item flexDirection="column">
                    <Label>
                      店舗名
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.shop_name`}
                      control={control}
                      rules={{ required: '店舗名を入力してください' }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.shop_name && (
                      <FieldError>{errors.shops[shopIndex]?.shop_name?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      店舗名（フリガナ）
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.shop_name_furigana`}
                      control={control}
                      rules={{ required: '店舗名（フリガナ）を入力してください' }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox
                          syncValue={value}
                          onChange={onChange}
                          onBlur={() => {
                        const next = toKatakana(value);
                        if (next !== value) onChange(next);
                      }}
                          fullWidth disabled={fieldsDisabled}
                        />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.shop_name_furigana && (
                      <FieldError>
                        {errors.shops[shopIndex]?.shop_name_furigana?.message}
                      </FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      郵便番号
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.postal_code`}
                      control={control}
                      rules={{
                        required: '郵便番号を入力してください',
                        pattern: {
                          value: /^\d{7}$/,
                          message: '7桁の数字を入力してください',
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={7} disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.postal_code && (
                      <FieldError>{errors.shops[shopIndex]?.postal_code?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      住所
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.address`}
                      control={control}
                      rules={{ required: '住所を入力してください' }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.address && (
                      <FieldError>{errors.shops[shopIndex]?.address?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      電話番号
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.tel`}
                      control={control}
                      rules={{
                        required: '電話番号を入力してください',
                        pattern: {
                          value: /^\d{10,11}$/,
                          message: '10桁または11桁の数字を入力してください',
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={11} disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.tel && (
                      <FieldError>{errors.shops[shopIndex]?.tel?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>メールアドレス</Label>
                    <Controller
                      name={`shops.${shopIndex}.email`}
                      control={control}
                      rules={{
                        validate: (value) => !value || validateEmail(value),
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.email && (
                      <FieldError>{errors.shops[shopIndex]?.email?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      担当者名
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.contact_person_name`}
                      control={control}
                      rules={{ required: '担当者名を入力してください' }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox syncValue={value} onChange={onChange} fullWidth disabled={fieldsDisabled} />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.contact_person_name && (
                      <FieldError>
                        {errors.shops[shopIndex]?.contact_person_name?.message}
                      </FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      担当者名（フリガナ）
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.contact_person_name_furigana`}
                      control={control}
                      rules={{ required: '担当者名（フリガナ）を入力してください' }}
                      render={({ field: { onChange, value } }) => (
                        <TextBox
                          syncValue={value}
                          onChange={onChange}
                          onBlur={() => {
                        const next = toKatakana(value);
                        if (next !== value) onChange(next);
                      }}
                          fullWidth disabled={fieldsDisabled}
                        />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.contact_person_name_furigana && (
                      <FieldError>
                        {errors.shops[shopIndex]?.contact_person_name_furigana?.message}
                      </FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>
                      店舗形態
                      <Required />
                    </Label>
                    <Controller
                      name={`shops.${shopIndex}.usage_type`}
                      control={control}
                      rules={{ required: '店舗形態を選択してください' }}
                      render={({ field: { onChange, value } }) => (
                        <Select<{ value: UsageType; label: string }, false, never>
                          options={USAGE_TYPE_OPTIONS}
                          value={USAGE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                          onChange={(opt) => onChange(opt ? opt.value : '')}
                          placeholder="店舗形態を選択してください"
                          isDisabled={fieldsDisabled}
                        />
                      )}
                    />
                    {errors.shops?.[shopIndex]?.usage_type && (
                      <FieldError>{errors.shops[shopIndex]?.usage_type?.message}</FieldError>
                    )}
                  </Grid.Item>
                  <Grid.Item flexDirection="column" nextRow size={2}>
                    <Label>
                      店舗ラベル
                      <Tooltip
                        id={`shop-label-${shopIndex}`}
                        text="申込フォームで申請者が選択した店舗ラベルです。審査時に変更できます。"
                        place="top"
                      />
                    </Label>
                    {tagGroupsLoadFailed ? (
                      // タググループ取得失敗時は「無効」と誤認させず、既存ラベルを保持する旨を表示
                      <InactiveTagNote>
                        店舗ラベル情報の取得に失敗しました。ページを再読み込みしてください。
                        （このまま更新しても付与済みのラベルは保持されます）
                      </InactiveTagNote>
                    ) : (
                      <>
                        <TagSelectionEditor
                          groups={availableTagGroups}
                          value={shopTags[shopIndex]?.selections ?? []}
                          requiredGroupIds={requiredTagGroupIds}
                          onChange={(sel) => setShopSelections(shopIndex, sel)}
                          // 編集可能なのは submitted / reviewing のみ（pending・確定済みは変更不可）
                          disabled={!isEditable}
                        />
                        {(shopTags[shopIndex]?.inactiveIds.length ?? 0) > 0 && (
                          <InactiveTagNote>
                            無効なラベル:{' '}
                            {(shopTags[shopIndex]?.inactiveIds ?? [])
                              .map((idv: string) => tagItemNameById[idv] || idv)
                              .join('、')}
                            （無効化されているため、更新・承認すると申請から外れます）
                          </InactiveTagNote>
                        )}
                      </>
                    )}
                  </Grid.Item>
                </Grid.Item>
              </React.Fragment>
            ))}

          </FormGrid>
        </form>

        {/* 審査中のみ: すべて読んだうえで保存・保留・判定する下部固定バー。
            フォーム外(Wrapper直下)に置くことで、長いフォームをスクロール中も下端に固定される。 */}
        {currentStatus === 'reviewing' && (
          <>
            {tagValidationErrors.length > 0 && (
              <Alert color="danger" style={{ marginTop: 16 }}>
                {tagValidationErrors.map((msg) => (
                  <div key={msg}>{msg}</div>
                ))}
              </Alert>
            )}
            <DecisionBar>
              <DecisionGuide>
                {isDirty
                  ? '未保存の変更があります。「変更を保存」するか「変更を破棄」してください。'
                  : '全ての情報を確認してから承認/却下してください'}
              </DecisionGuide>
              <DecisionButton
                type="button"
                variant="muted-outline"
                onClick={() => setIsDiscardOpen(true)}
                disabled={!isDirty}
              >
                変更を破棄
              </DecisionButton>
              <DecisionButton type="button" variant="outline" onClick={handleSave} disabled={!isDirty}>
                変更を保存
              </DecisionButton>
              <DecisionButton type="button" variant="muted-outline" onClick={hold} disabled={isDirty}>
                保留
              </DecisionButton>
              <DecisionButton type="button" variant="danger" onClick={handleReject} disabled={isDirty}>
                却下
              </DecisionButton>
              <DecisionButton
                type="button"
                variant="contained"
                onClick={handleApprove}
                disabled={isDirty || !isAutoNumberingAvailable}
              >
                承認
              </DecisionButton>
            </DecisionBar>
          </>
        )}

        {/* 結果フィードバックは画面上部の固定トースト（3 秒で自動クローズ・× で即閉じ）。 */}
        <NotificationToast
          isOpen={submitResult !== null}
          variant={submitResult === 'error' ? 'error' : 'success'}
          message={
            submitResult === 'error'
              ? errorMessage
              : submitResult
              ? SUBMIT_MESSAGES[submitResult]
              : ''
          }
          onClose={() => setSubmitResult(null)}
        />
        {/* 下部固定バー（DecisionBar）と本文が重ならないよう余白を確保する。 */}
        <div style={{ marginBottom: '120px' }} />
      </Wrapper>

      <DiscardChangesModal
        isOpen={isDiscardOpen}
        onCancel={() => setIsDiscardOpen(false)}
        onConfirm={confirmDiscard}
      />

      <ConfirmModal
        isOpen={isModalOpen}
        formValues={pendingFormValues}
        originalValues={originalValues}
        isSaveOnly={!!pendingFormValues && pendingFormValues.status === currentStatus}
        organizationCode={application?.organization_code ?? null}
        privateMoneyNames={selectedPrivateMoneyNames}
        shopTagSummaries={shopTags.map((st) => {
          // 行は「元の選択 ∪ 現在の選択」のグループで組む。アイテムを全て消したグループも
          // 行として残し、内容を `-` で見せる（消えると承認者が削除に気づけないため）。
          const selectedGroupIds = new Set(
            st.selections.map((s) => s.groupId).filter((gid): gid is string => gid !== null)
          );
          return availableTagGroups
            .filter(
              (group) =>
                selectedGroupIds.has(group.id) ||
                group.items.some((item) => st.originalIds.includes(item.id))
            )
            .map((group) => {
              const groupItemIdSet = new Set(group.items.map((item) => item.id));
              const currentIds = st.selections.find((s) => s.groupId === group.id)?.itemIds ?? [];
              const originalForGroup = st.originalIds.filter((id) => groupItemIdSet.has(id)).sort();
              const currentForGroup = [...currentIds].sort();
              return {
                groupId: group.id,
                groupName: group.name,
                itemNames: currentIds
                  .map((id) => group.items.find((item) => item.id === id)?.name ?? '')
                  .filter(Boolean),
                changed: JSON.stringify(originalForGroup) !== JSON.stringify(currentForGroup),
              };
            });
        })}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default MerchantApplicationDetail;
